export type StorePlace = {
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  loc?: number[];
};

export type StoreCoords = { lat: number; lng: number };

/**
 * Mongo/GeoJSON costuma guardar [lng, lat]. Google Maps espera lat,lng.
 * No Brasil |lng| (~35–74) costuma ser maior que |lat| (~0–34).
 */
export function normalizeStoreCoords(
  loc: number[] | undefined | null
): StoreCoords | null {
  if (!loc || loc.length !== 2) return null;
  const [a, b] = loc;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  // Valores fora do range de latitude → primeiro valor é longitude
  if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
    return { lat: b, lng: a };
  }
  if (Math.abs(b) > 90 && Math.abs(a) <= 90) {
    return { lat: a, lng: b };
  }

  // Heurística BR/GeoJSON: |lng| tipicamente > |lat|
  if (Math.abs(a) > 30 && Math.abs(a) > Math.abs(b)) {
    return { lat: b, lng: a };
  }

  return { lat: a, lng: b };
}

function includesIgnoreCase(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase('pt-BR').includes(needle.toLocaleLowerCase('pt-BR'));
}

/**
 * Monta query de endereço sem duplicar cidade/UF/CEP já presentes no address.
 */
export function buildStoreAddressQuery(place: StorePlace | undefined): string {
  if (!place) return '';

  const address = place.address?.trim() || '';
  const extras = [place.city, place.state, place.zipCode]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part && !includesIgnoreCase(address, part));

  return [address, ...extras].filter(Boolean).join(', ');
}

export function getStoreMapUrls(place: StorePlace | undefined) {
  if (!place) {
    return { embedUrl: '', directionsUrl: '' };
  }

  const addressQuery = buildStoreAddressQuery(place);
  const coords = normalizeStoreCoords(place.loc);

  // Preferir endereço (bate com o texto exibido e evita coords erradas no cadastro).
  // Coords normalizadas só como fallback — e sempre na ordem lat,lng do Google.
  const destination = addressQuery || (coords ? `${coords.lat},${coords.lng}` : '');

  if (!destination) {
    return { embedUrl: '', directionsUrl: '' };
  }

  const encoded = encodeURIComponent(destination);

  return {
    embedUrl: `https://maps.google.com/maps?q=${encoded}&hl=pt-BR&z=16&output=embed`,
    directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${encoded}`,
  };
}

export function openStoreDirections(place: StorePlace | undefined) {
  const { directionsUrl } = getStoreMapUrls(place);
  if (directionsUrl) {
    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  }
}
